import Button from "~/components/ui/Button"
import Icon from "~/components/ui/Icon"
import { useComputed, useSignal } from "~/sdk/signal"
import { useUser } from "~/hooks/useUser"
import { useWishlist } from "~/hooks/useWishlist"
import { sendEvent } from "~/sdk/analytics"
import { useId } from "react"
import type { JSX } from "react"
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics"
import type { ToAddToWishlist } from "~/sdk/ga4/events/add_to_wishlist"

export interface Props {
    productID: string
    sku: string
    variant?: 'icon' | 'full' | 'pdp'
    analytics?: ToAddToWishlist
}

function WishlistButton(
    { variant = 'icon', productID, sku, analytics }: Props,
) {
    const { user } = useUser()
    const item = { sku, productId: productID }
    const { loading, addItem, removeItem, getItem } = useWishlist()
    const listItem = useComputed(() => getItem(item))
    const fetching = useSignal(false)
    const id = useId()

    const isUserLoggedIn = Boolean(user?.value?.email)
    const inWishlist = Boolean(listItem.value)

    async function onClick(e: React.MouseEvent<HTMLButtonElement>) {
        e.stopPropagation()
        e.preventDefault()

        if (!isUserLoggedIn) {
            location.href = `/login?returnUrl=${globalThis.location.pathname}`
            return
        }

        if (loading.value) {
            return
        }

        try {
            fetching.value = true

            if (inWishlist) {
                await removeItem({ id: listItem.value!.id }!)
            } else {
                await addItem(item)

                if (analytics) {
                    const addToWishlistEvent = toAnalytics({
                        type: 'add_to_wishlist',
                        data: analytics,
                    })

                    sendEvent(addToWishlistEvent)
                }
            }
        } finally {
            fetching.value = false
        }
    }

    if (fetching.value) {
        return variant === 'pdp'
            ? <span>Carregando...</span>
            : <span className='loading loading-spinner loading-xs' />
    }

    return (
        <>
            {variant === 'pdp'
                ? (
                    <button
                        id={id}
                        type='button'
                        className='flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity disabled:cursor-not-allowed'
                        onClick={onClick}
                        disabled={fetching.value}
                    >
                        <Icon
                            id='Heart'
                            size={14}
                            className={inWishlist ? 'text-salmon' : 'text-[#acacac]'}
                        />

                        {fetching.value && !inWishlist && (
                            <span>Adicionando à lista de desejos</span>
                        )}
                        {fetching.value && inWishlist && <span>Removendo da lista de desejos</span>}
                        {!fetching.value && inWishlist && (
                            <span className='font-bold'>Adicionado à lista de desejos</span>
                        )}
                        {!fetching.value && !inWishlist && (
                            <span className='font-bold'>Adicionar à lista de desejos</span>
                        )}
                    </button>
                )
                : (
                    <Button
                        id={id}
                        className={variant === 'icon'
                            ? 'btn-circle btn-ghost gap-2'
                            : 'btn-primary btn-outline gap-2'}
                        loading={fetching.value}
                        // A variante default já tem texto visível ("Favoritar" /
                        // "Remover"); só a `icon` precisa de nome, e ele tem que
                        // acompanhar o estado.
                        aria-label={variant === 'icon'
                            ? (inWishlist
                                ? 'Remover da lista de desejos'
                                : 'Adicionar à lista de desejos')
                            : undefined}
                        onClick={onClick}
                    >
                        <Icon
                            id='Heart'
                            size={24}
                            strokeWidth={2}
                            className={inWishlist ? 'text-salmon' : 'black'}
                        />
                        {variant === 'icon' ? null : inWishlist ? 'Remover' : 'Favoritar'}
                    </Button>
                )}
        </>
    )
}

export default WishlistButton
